import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import TableSearchInput from '../components/TableSearchInput';
import SearchableSelectWithAdd from '../components/SearchableSelectWithAdd';
import BulkActionsBar, { bulkIcons } from '../components/BulkActionsBar';
import BulkValueModal from '../components/BulkValueModal';
import { FINANCIALS_LABOUR_CATEGORIES } from '../constants/financialsLabour';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const initialsFor = (name = '') =>
  String(name || '')
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'VD';

// ── Add Vendor Modal ──────────────────────────────────────────────────────────
function AddVendorModal({ vendorTypes, vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    displayName: '',
    panNumber: '',
    status: 'active',
    address: { line1: '', line2: '', state: '', city: '', pincode: '' },
    vendorType: '',
    documents: [],
  });
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const docInputRef = useRef(null);

  const vendorTypeOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    const add = (s) => {
      const t = String(s || '').trim();
      if (!t) return;
      const k = t.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(t);
    };
    FINANCIALS_LABOUR_CATEGORIES.forEach(add);
    (vendorTypes || []).forEach(add);
    return out.sort((a, b) => a.localeCompare(b));
  }, [vendorTypes]);

  useEffect(() => {
    if (vendor) {
      setForm({
        displayName: vendor.displayName || '',
        panNumber: vendor.panNumber || '',
        status: vendor.status || 'active',
        address: {
          line1: vendor.address?.line1 || '',
          line2: vendor.address?.line2 || '',
          state: vendor.address?.state || '',
          city: vendor.address?.city || '',
          pincode: vendor.address?.pincode || '',
        },
        vendorType: vendor.vendorType || '',
        documents: Array.isArray(vendor.documents) ? vendor.documents.map((d) => ({ name: d.name || '', url: d.url || '' })) : [],
      });
      setContacts(vendor.contacts || []);
    } else {
      setForm({
        displayName: '',
        panNumber: '',
        status: 'active',
        address: { line1: '', line2: '', state: '', city: '', pincode: '' },
        vendorType: '',
        documents: [],
      });
      setContacts([]);
      setNewContact({ name: '', email: '', phone: '' });
    }
  }, [vendor]);

  const h = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const hAddr = (e) => setForm((f) => ({ ...f, address: { ...f.address, [e.target.name]: e.target.value } }));

  const addContact = () => {
    if (!newContact.name) return;
    setContacts((c) => [...c, newContact]);
    setNewContact({ name: '', email: '', phone: '' });
  };

  const removeContact = (i) => setContacts((c) => c.filter((_, idx) => idx !== i));

  const addDocumentsFromFiles = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (!files.length) return;
    setForm((f) => ({
      ...f,
      documents: [
        ...f.documents,
        ...files.map((file) => ({ name: file.name, url: '' })),
      ],
    }));
  };

  const removeDocument = (i) => {
    setForm((f) => ({ ...f, documents: f.documents.filter((_, idx) => idx !== i) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.vendorType?.trim()) {
      setError('Vendor type is required.');
      return;
    }
    if (!form.address.city?.trim()) {
      setError('City is required.');
      return;
    }
    if (!form.address.line1?.trim()) {
      setError('Address line 1 is required.');
      return;
    }
    if (!form.address.state?.trim()) {
      setError('State is required.');
      return;
    }
    if (!form.documents?.length) {
      setError('Add at least one vendor document.');
      return;
    }
    setSaving(true);
    const payload = {
      displayName: form.displayName.trim(),
      panNumber: form.panNumber?.trim() || undefined,
      status: form.status,
      address: {
        line1: form.address.line1.trim(),
        line2: form.address.line2?.trim() || undefined,
        state: form.address.state?.trim(),
        city: form.address.city?.trim(),
        pincode: form.address.pincode?.trim() || undefined,
      },
      contacts,
      vendorType: form.vendorType.trim(),
      documents: form.documents.map((d) => ({ name: d.name, url: d.url || '' })),
    };
    try {
      if (vendor) {
        await api.put(`/vendors/${vendor._id}`, payload);
      } else {
        await api.post('/vendors', payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save vendor.');
    } finally {
      setSaving(false);
    }
  };

  const iS = { width:'100%', padding:'8px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', fontFamily:'inherit', background:'#F9FAFB' };
  const lS = { display:'block', fontSize:11, fontWeight:600, color:'#6B7280', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.05em' };
  const secS = { display:'flex', alignItems:'center', gap:8, fontSize:14, fontWeight:700, color:'#1F2937', marginBottom:14, marginTop:4 };
  const numS = { width:22, height:22, borderRadius:'50%', background:'#976793', color:'#fff', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16 }}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:540,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'18px 24px 0' }}>
          <h3 style={{ margin:0,fontSize:18,fontWeight:700,color:'#1F2937' }}>  {vendor ? 'Edit Vendor' : 'New Vendor'}
</h3>
          <button onClick={onClose} style={{ background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#9CA3AF' }}>×</button>
        </div>

        <div style={{ padding:'16px 24px 0' }}>
          {error && <div style={{ background:'#FEE2E2',border:'1px solid #FECACA',color:'#EF4444',padding:'10px 14px',borderRadius:8,fontSize:13,marginBottom:14 }}>{error}</div>}
          <form id="vendor-form" onSubmit={submit}>

            {/* 1. Primary Details */}
            <div style={secS}><div style={numS}>1</div> Primary Details</div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16 }}>
              <div><label style={lS}>Display Name *</label><input style={iS} name="displayName" value={form.displayName} onChange={h} required /></div>
              <div><label style={lS}>PAN Number</label><input style={iS} name="panNumber" value={form.panNumber} onChange={h} placeholder="Optional" /></div>
              <div><label style={lS}>Status *</label>
                <select style={iS} name="status" value={form.status} onChange={h} required>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="blacklist">Blacklist</option>
                </select>
              </div>
            </div>

            {/* 2. Address Details */}
            <div style={secS}><div style={numS}>2</div> Address Details</div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12 }}>
              <div><label style={lS}>Address Line 1 *</label><input style={iS} name="line1" value={form.address.line1} onChange={hAddr} required /></div>
              <div><label style={lS}>Address Line 2</label><input style={iS} name="line2" value={form.address.line2} onChange={hAddr} /></div>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:16 }}>
              <div><label style={lS}>State *</label>
                <select style={iS} name="state" value={form.address.state} onChange={hAddr} required>
                  <option value="">Select</option>
                  {['Maharashtra','Gujarat','Karnataka','Tamil Nadu','Delhi','Rajasthan','UP','MP','Telangana','Goa','Punjab'].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label style={lS}>City *</label>
                <select style={iS} name="city" value={form.address.city} onChange={hAddr} required>
                  <option value="">Select</option>
                  {['Pune','Mumbai','Nashik','Nagpur','Aurangabad','Thane','Kolhapur','Solapur'].map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div><label style={lS}>Pincode</label><input style={iS} name="pincode" value={form.address.pincode} onChange={hAddr} /></div>
            </div>

            {/* 3. Contacts */}
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12 }}>
              <div style={secS}><div style={numS}>3</div> Contacts</div>
              <button type="button" onClick={addContact}
                style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 12px',border:'1px solid #976793',borderRadius:8,background:'#fff',color:'#976793',fontSize:12,fontWeight:600,cursor:'pointer' }}>
                + Add Contact
              </button>
            </div>
            {/* New contact inputs */}
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8 }}>
              <input style={iS} placeholder="Name" value={newContact.name} onChange={e=>setNewContact(c=>({...c,name:e.target.value}))} />
              <input style={iS} placeholder="E-Mail" value={newContact.email} onChange={e=>setNewContact(c=>({...c,email:e.target.value}))} />
              <input style={iS} placeholder="Phone" value={newContact.phone} onChange={e=>setNewContact(c=>({...c,phone:e.target.value}))} />
            </div>
            {/* Contact table */}
            <div style={{ border:'1px solid #E5E7EB',borderRadius:8,overflow:'hidden',marginBottom:16 }}>
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead><tr>
                  {['Name','E-Mail','Phone','Actions'].map(h=><th key={h} style={{ padding:'8px 12px',textAlign:'left',fontSize:11,fontWeight:600,color:'#6B7280',background:'#F9FAFB',borderBottom:'1px solid #E5E7EB' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign:'center',padding:'16px',color:'#9CA3AF',fontSize:12 }}>No data available</td></tr>
                  ) : contacts.map((c,i)=>(
                    <tr key={i}><td style={{ padding:'8px 12px' }}>{c.name}</td><td style={{ padding:'8px 12px' }}>{c.email}</td><td style={{ padding:'8px 12px' }}>{c.phone}</td>
                      <td style={{ padding:'8px 12px' }}>
                        <button type="button" onClick={()=>removeContact(i)} style={{ background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:12 }}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 4. Vendor Type */}
            <div style={secS}><div style={numS}>4</div> Vendor Type</div>
            <div style={{ marginBottom:16 }}>
              <label style={lS}>Vendor Type *</label>
              <p style={{ fontSize:11,color:'#9CA3AF',margin:'0 0 6px' }}>Search Financials labour categories and saved vendor types. Type a new name and use + Add if it is not listed.</p>
              <SearchableSelectWithAdd
                value={form.vendorType}
                onChange={(v) => setForm((f) => ({ ...f, vendorType: v }))}
                options={vendorTypeOptions}
                onAddNew={async () => {}}
                addPrompt="as vendor type"
                placeholder="Search or add vendor type…"
                style={{ width:'100%' }}
              />
            </div>

            {/* 5. Documents */}
            <div style={secS}><div style={numS}>5</div> Documents *</div>
            <div style={{ border:'1px solid #E5E7EB',borderRadius:10,overflow:'hidden',marginBottom:16 }}>
              <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#F9FAFB',borderBottom:'1px solid #E5E7EB' }}>
                <span style={{ fontSize:13,fontWeight:600,color:'#374151' }}>Vendor Documents</span>
                <div>
                  <input ref={docInputRef} type="file" multiple style={{ display:'none' }} onChange={addDocumentsFromFiles} />
                  <button
                    type="button"
                    onClick={() => docInputRef.current?.click()}
                    style={{ background:'none',border:'none',color:'#976793',fontSize:12,fontWeight:600,cursor:'pointer' }}
                  >
                    + Add Documents
                  </button>
                </div>
              </div>
              {form.documents.length === 0 ? (
                <div style={{ padding:'20px',textAlign:'center',color:'#9CA3AF',fontSize:12 }}>No documents — add at least one file.</div>
              ) : (
                <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                  <thead>
                    <tr>
                      {['File name','Actions'].map((h) => (
                        <th key={h} style={{ padding:'8px 12px',textAlign:'left',fontSize:11,fontWeight:600,color:'#6B7280',background:'#fff',borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.documents.map((d, i) => (
                      <tr key={i}>
                        <td style={{ padding:'8px 12px' }}>{d.name || '—'}</td>
                        <td style={{ padding:'8px 12px' }}>
                          <button type="button" onClick={() => removeDocument(i)} style={{ background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:12 }}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </form>
        </div>

        <div style={{ padding:'12px 24px 20px',display:'flex',justifyContent:'flex-end',borderTop:'1px solid #F3F4F6' }}>
          <button type="button" onClick={onClose} style={{ padding:'8px 18px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,cursor:'pointer',marginRight:8,color:'#374151' }}>Cancel</button>
          <button type="submit" form="vendor-form" disabled={saving}
            style={{ padding:'8px 22px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
            {saving ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Vendors Page ─────────────────────────────────────────────────────────
export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors]     = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [limit, setLimit]         = useState(25);
  const [showModal, setShowModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [colSearch, setColSearch] = useState({});
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const canDelete = user?.role === 'admin';
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkValue, setBulkValue] = useState('');

  const loadTypes = useCallback(async () => {
    try { const r = await api.get('/vendors/types'); setVendorTypes(r.data.data.types || []); } catch{}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (activeTab !== 'all') params.status = activeTab;
      if (search) params.search = search;
      const r = await api.get('/vendors', { params });
      setVendors(r.data.data.vendors || []);
      setTotal(r.data.data.pagination?.total || 0);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, limit, activeTab, search]);

  useEffect(() => { load(); loadTypes(); }, [load, loadTypes]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => vendors.some((v) => v._id === id)));
  }, [vendors]);

  const displayVendors = useMemo(() => {
    const fieldMap = (v) => ({
      vendorCode: v.vendorCode,
      displayName: v.displayName,
      pan: v.panNumber || '',
      gst: v.gstNumber,
      legalName: v.legalName,
      phone: v.contacts?.[0]?.phone,
      city: v.address?.city,
    });
    return vendors.filter((v) => {
      const m = fieldMap(v);
      return Object.entries(colSearch).every(([k, raw]) => {
        const q = String(raw || '').trim().toLowerCase();
        if (!q) return true;
        return String(m[k] ?? '').toLowerCase().includes(q);
      });
    });
  }, [vendors, colSearch]);

  const STATUS_COUNTS = { all: total };
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedVendors = useMemo(() => vendors.filter((v) => selectedSet.has(v._id)), [vendors, selectedSet]);
  const allVisibleSelected = displayVendors.length > 0 && displayVendors.every((v) => selectedSet.has(v._id));
  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id));
  };
  const selectAllVisible = () => setSelectedIds(displayVendors.map((v) => v._id));
  const clearSelection = () => setSelectedIds([]);
  const openBulkEdit = () => {
    const first = selectedVendors[0];
    if (!first) return;
    setSelectedVendor(first);
    setShowModal(true);
  };
  const openBulkModal = (kind) => {
    const first = selectedVendors[0];
    setBulkValue(kind === 'status' ? (first?.status || 'active') : (first?.vendorType || ''));
    setBulkModal(kind);
  };

  const handleBulkDelete = async () => {
    if (!canDelete || selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected vendor(s)?`)) return;
    try {
      await Promise.all(selectedIds.map((id) => api.delete(`/vendors/${id}`)));
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.message || 'Bulk delete failed.');
    }
  };

  const applyBulkUpdate = async () => {
    const value = String(bulkValue || '').trim();
    try {
      await Promise.all(selectedIds.map((id) => api.put(`/vendors/${id}`, { [bulkModal]: value })));
      setBulkModal(null);
      load();
      loadTypes();
    } catch (e) {
      alert(e.response?.data?.message || 'Bulk update failed.');
    }
  };

  const TABS = [
    { key:'all',       label:`All (${total})` },
    { key:'active',    label:`Active (${vendors.filter(v=>v.status==='active').length})` },
    { key:'inactive',  label:`Inactive (${vendors.filter(v=>v.status==='inactive').length})` },
    { key:'blacklist', label:`Blacklist (${vendors.filter(v=>v.status==='blacklist').length})` },
  ];

  const thS = { textAlign:'left', padding:'8px 10px', fontSize:11, fontWeight:600, color:'#6B7280', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap', background:'#fff', position:'sticky', top:0, zIndex:1 };
  const tdS = { padding:'9px 10px', fontSize:12, color:'#374151', borderBottom:'1px solid #F3F4F6', verticalAlign:'middle' };
  const COLS = ['Created On','Vendor Code','Display name','PAN','GST Number','Legal Name','Phone','City','Vendor Type','Status','View Details'];

  return (
    <div style={{ display:'flex',flexDirection:'column',height:'100%',background:'#fff',fontFamily:'inherit' }}>
      {/* Status Tabs */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',borderBottom:'1px solid #E5E7EB',flexWrap:'wrap',gap:10 }}>
        <div style={{ display:'flex',gap:6,flexWrap:'wrap',alignItems:'center' }}>
          <TableSearchInput
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search vendors (server)…"
            style={{ marginBottom: 0, minWidth: 200 }}
          />
          {TABS.map(t => (
            <button key={t.key} onClick={()=>{ setActiveTab(t.key); setPage(1); }}
              style={{ padding:'6px 14px',borderRadius:20,border:'none',fontSize:13,fontWeight:500,cursor:'pointer',transition:'all 0.15s',
                background: activeTab===t.key ? '#976793' : '#F3F4F6',
                color:      activeTab===t.key ? '#fff' : '#374151',
              }}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display:'flex',gap:8 }}>
          {isAdmin && (
<button onClick={() => {
  setSelectedVendor(null);
  setShowModal(true);
}}              style={{ display:'flex',alignItems:'center',gap:6,padding:'7px 16px',background:'#976793',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' }}>
              + Add Vendor
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </button>
          )}
          <button style={{ display:'flex',alignItems:'center',gap:6,padding:'7px 14px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',color:'#374151' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex:1,overflowX:'auto',overflowY:'auto' }}>
        {loading ? (
          <div style={{ textAlign:'center',padding:'4rem' }}>
            <div style={{ width:32,height:32,border:'3px solid #E5E7EB',borderTopColor:'#976793',borderRadius:'50%',animation:'spin 0.7s linear infinite',margin:'0 auto' }} />
          </div>
        ) : (
          <>
          <div className="tablet-split-view">
            <div className="tablet-list-pane">
              <div className="tablet-pane-title">Vendors</div>
              {displayVendors.map((v, index) => (
                <div className="mobile-card-row" key={v._id} style={index === 0 ? { background:'var(--mauve-pale)', borderRadius:8, padding:'0.75rem' } : null}>
                  <div className="mobile-avatar">{initialsFor(v.displayName)}</div>
                  <div className="mobile-row-main">
                    <span className="mobile-row-title">{v.displayName || 'Unnamed vendor'}</span>
                    <span className="mobile-row-sub">{v.vendorCode || 'No code'} · {v.vendorType || 'Vendor'}</span>
                  </div>
                  <span className={`badge badge-${v.status === 'active' ? 'active' : 'inactive'}`}>{v.status || 'active'}</span>
                </div>
              ))}
            </div>
            <div className="tablet-detail-pane">
              {displayVendors[0] ? (
                <>
                  <div className="tablet-detail-title">{displayVendors[0].displayName || 'Vendor'}</div>
                  <div className="tablet-detail-sub">{displayVendors[0].vendorCode || 'No code'} · {displayVendors[0].vendorType || 'Vendor'} · {displayVendors[0].address?.city || 'No city'}</div>
                  <div className="stats-grid" style={{ gridTemplateColumns:'1fr 1fr', marginBottom:'1rem' }}>
                    <div className="stat-card"><div className="stat-num">{displayVendors[0].panNumber || 'N/A'}</div><div className="stat-label">PAN</div></div>
                    <div className="stat-card"><div className="stat-num">{displayVendors[0].status || 'active'}</div><div className="stat-label">Status</div></div>
                  </div>
                  <button className="btn btn-primary" onClick={() => { setSelectedVendor(displayVendors[0]); setShowModal(true); }}>Edit Details</button>
                </>
              ) : (
                <div className="tablet-empty-panel">No vendors found</div>
              )}
            </div>
          </div>
          <div className="mobile-card-list" style={{ padding:'0 14px 12px' }}>
            {displayVendors.length === 0 ? (
              <div className="empty-state" style={{ padding:'2rem 1rem' }}>
                <h3>No vendors found</h3>
                <p>Try a different search or filter.</p>
              </div>
            ) : displayVendors.map(v => (
              <div className="mobile-card-row" key={v._id}>
                <div className="mobile-avatar">{initialsFor(v.displayName)}</div>
                <div className="mobile-row-main">
                  <span className="mobile-row-title">{v.displayName || 'Unnamed vendor'}</span>
                  <span className="mobile-row-sub">{v.vendorCode || 'No code'} · {v.vendorType || 'Vendor'} · {v.address?.city || 'No city'}</span>
                </div>
                <div className="mobile-row-end">
                  <span className={`badge badge-${v.status === 'active' ? 'active' : 'inactive'}`}>{v.status || 'active'}</span>
                </div>
              </div>
            ))}
          </div>
          <table className="desktop-table-mobile-hidden tablet-table-hidden" style={{ width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:1100 }}>
            <thead>
              <tr>
                {isAdmin && (
                  <th style={{ ...thS, width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                      aria-label="Select all visible vendors"
                    />
                  </th>
                )}
                {COLS.map(col => (
                  <th key={col} style={thS}>
                    <div style={{ display:'flex',alignItems:'center',gap:4 }}>
                      {col === 'Created On' && <span>📅</span>}
                      {col}
                      {col === 'Vendor Type' && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              {/* Inline search row */}
              <tr style={{ background:'#F9FAFB' }}>
                {isAdmin && (
                  <td style={{ padding:'4px 8px',borderBottom:'1px solid #E5E7EB' }} />
                )}
                <td style={{ padding:'4px 8px',borderBottom:'1px solid #E5E7EB' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/></svg>
                </td>
                {['vendorCode','displayName','pan','gst','legalName','phone','city'].map(f => (
                  <td key={f} style={{ padding:'4px 8px',borderBottom:'1px solid #E5E7EB' }}>
                    <div style={{ display:'flex',alignItems:'center',gap:3 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                      <input style={{ width:'100%',border:'1px solid #E5E7EB',borderRadius:4,padding:'2px 5px',fontSize:10,outline:'none',fontFamily:'inherit' }}
                        value={colSearch[f] || ''}
                        onChange={e=>setColSearch(s=>({...s,[f]:e.target.value}))} />
                    </div>
                  </td>
                ))}
                {/* Vendor type with filter */}
                <td style={{ padding:'4px 8px',borderBottom:'1px solid #E5E7EB' }}>
                  <div style={{ display:'flex',alignItems:'center',gap:3 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                  </div>
                </td>
                {/* Status with filter */}
                <td style={{ padding:'4px 8px',borderBottom:'1px solid #E5E7EB' }}>
                  <div style={{ display:'flex',alignItems:'center',gap:3 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  </div>
                </td>
                <td style={{ borderBottom:'1px solid #E5E7EB' }} />
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 ? (
                <tr><td colSpan={isAdmin ? 12 : 11} style={{ textAlign:'center',padding:'4rem',color:'#9CA3AF' }}>No vendors found</td></tr>
              ) : displayVendors.length === 0 ? (
                <tr><td colSpan={isAdmin ? 12 : 11} style={{ textAlign:'center',padding:'3rem',color:'#9CA3AF',fontSize:13 }}>No rows match column filters</td></tr>
              ) : displayVendors.map(v => (
                <tr key={v._id}
                  onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                  onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                  {isAdmin && (
                    <td style={tdS} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(v._id)}
                        onChange={(e) => toggleSelected(v._id, e.target.checked)}
                        aria-label={`Select ${v.displayName}`}
                      />
                    </td>
                  )}
                  <td style={{...tdS,color:'#6B7280',fontSize:11,whiteSpace:'nowrap'}}>
                    <div>{new Date(v.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                  </td>
                  <td style={{...tdS,fontFamily:'monospace',fontSize:11,color:'#6B7280'}}>{v.vendorCode||'—'}</td>
                  <td style={{...tdS,fontWeight:500,color:'#1F2937'}}>{v.displayName}</td>
                  <td style={tdS}>
                    <span style={{ background:'#F3F4F6',padding:'2px 8px',borderRadius:20,fontSize:10,color:'#6B7280' }}>
                      {v.panNumber || 'N/A'}
                    </span>
                  </td>
                  <td style={tdS}>
                    <span style={{ background:'#F3F4F6',padding:'2px 8px',borderRadius:20,fontSize:10,color:'#6B7280' }}>
                      {v.gstNumber || 'N/A'}
                    </span>
                  </td>
                  <td style={{...tdS,color:'#6B7280'}}>{v.legalName||'—'}</td>
                  <td style={{...tdS,color:'#6B7280'}}>{v.contacts?.[0]?.phone || '—'}</td>
                  <td style={{...tdS,color:'#6B7280'}}>{v.address?.city||'—'}</td>
                  <td style={tdS}>
                    {v.vendorType && (
                      <span style={{ background:'#F7F1F7',color:'#976793',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:500 }}>{v.vendorType}</span>
                    )}
                  </td>
                  <td style={tdS}>
                    <span style={{ background: v.status==='active'?'#D1FAE5':v.status==='blacklist'?'#FEE2E2':'#F3F4F6', color: v.status==='active'?'#059669':v.status==='blacklist'?'#EF4444':'#6B7280', padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,textTransform:'capitalize' }}>
                      {v.status==='active'?'Active':v.status==='blacklist'?'Blacklist':'Inactive'}
                    </span>
                  </td>
                  <td style={tdS}>
  <button
    onClick={() => {
  setSelectedVendor(v);
  setShowModal(true);
}}
    style={{
      width:28,
      height:28,
      border:'1px solid #DBEAFE',
      borderRadius:6,
      background:'#F7F1F7',
      cursor:'pointer',
      display:'flex',
      alignItems:'center',
      justifyContent:'center',
      color:'#3B82F6'
    }}
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  </button>
</td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Pagination */}
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',borderTop:'1px solid #E5E7EB',fontSize:12,color:'#6B7280',flexShrink:0 }}>
        <span>{total} items</span>
        <div style={{ display:'flex',alignItems:'center',gap:4 }}>
          <button disabled={page===1} onClick={()=>setPage(p=>p-1)} style={{ width:26,height:26,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:page===1?'not-allowed':'pointer',color:page===1?'#D1D5DB':'#374151',display:'flex',alignItems:'center',justifyContent:'center' }}>‹</button>
          <span style={{ padding:'0 8px' }}>{page}</span>
          <button disabled={page*limit>=total} onClick={()=>setPage(p=>p+1)} style={{ width:26,height:26,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:page*limit>=total?'not-allowed':'pointer',color:page*limit>=total?'#D1D5DB':'#374151',display:'flex',alignItems:'center',justifyContent:'center' }}>›</button>
          <select value={limit} onChange={e=>{setLimit(Number(e.target.value));setPage(1);}} style={{ marginLeft:8,padding:'4px 8px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:12,outline:'none' }}>
            {[10,25,50].map(n=><option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={load} style={{ width:26,height:26,border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#9CA3AF',marginLeft:4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          </button>
        </div>
      </div>

{showModal && (
  <AddVendorModal
    vendorTypes={vendorTypes}
    vendor={selectedVendor}
    onClose={() => setShowModal(false)}
    onSaved={() => { load(); loadTypes(); }}
  />
)}

      <BulkActionsBar
        count={selectedIds.length}
        actions={[
          { key: 'edit', label: 'Edit First', icon: bulkIcons.edit, onClick: openBulkEdit },
          { key: 'status', label: 'Set Status', icon: bulkIcons.set, onClick: () => openBulkModal('status') },
          { key: 'type', label: 'Set Vendor Type', icon: bulkIcons.set, onClick: () => openBulkModal('vendorType') },
          { key: 'all', label: 'Select All', icon: bulkIcons.selectAll, onClick: selectAllVisible },
          { key: 'none', label: 'Deselect All', icon: bulkIcons.clear, onClick: clearSelection },
          canDelete && { key: 'delete', label: 'Delete', icon: bulkIcons.delete, onClick: handleBulkDelete, danger: true },
        ]}
      />

      {bulkModal && (
        <BulkValueModal
          title={bulkModal === 'status' ? 'Set status' : 'Set vendor type'}
          count={selectedIds.length}
          value={bulkValue}
          onChange={setBulkValue}
          onClose={() => setBulkModal(null)}
          onApply={applyBulkUpdate}
          options={bulkModal === 'status' ? [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'blacklist', label: 'Blacklist' },
          ] : vendorTypes}
          placeholder="Vendor type"
        />
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
